import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  Search, 
  AlertCircle, 
  CheckCircle2, 
  Clock,
  Download,
  Bug,
  Smartphone,
  Trash2,
  X,
  Upload,
  Image as ImageIcon,
  FileText,
  Edit2,
  Eye,
  Calendar,
  Lightbulb,
  Sparkles,
  ThumbsUp,
  Star
} from 'lucide-react';
import ProjectAnalysis from './ProjectAnalysis';

const API_URL = import.meta.env.VITE_API_URL || 'https://bugtracker-yn0x.onrender.com/api';

// --- Components ---

const SeverityBadge = ({ level }) => {
  const styles = {
    Critical: 'bg-red-100 text-red-800 border-red-200',
    High: 'bg-orange-100 text-orange-800 border-orange-200',
    Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    Low: 'bg-green-100 text-green-800 border-green-200',
  };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[level] || styles.Low}`}>
      {level}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    Open: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    'In Progress': 'bg-purple-50 text-purple-700 ring-purple-600/20',
    Resolved: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    Closed: 'bg-gray-50 text-gray-600 ring-gray-500/10',
  };

  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${styles[status] || styles.Open}`}>
      {status}
    </span>
  );
};

const AddBugModal = ({ isOpen, onClose, onSubmit, isSubmitting, selectedImage, onImageSelect }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Bug className="w-5 h-5 text-indigo-600" />
            Log New Defect
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Defect Title</label>
            <input 
              name="title" 
              required 
              placeholder="e.g., App crashes on login screen"
              className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select name="severity" className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none">
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Device / OS</label>
              <input 
                name="device" 
                placeholder="e.g., iPhone 14, iOS 16"
                className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description & Steps to Reproduce</label>
            <textarea 
              name="description" 
              required 
              rows={4}
              placeholder="1. Open app&#10;2. Click settings&#10;3. Observe crash..."
              className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-indigo-700 mb-1">Desired Solution (Optional)</label>
            <textarea
              name="desiredSolution"
              rows={3}
              placeholder="Describe what solution you want, e.g. expected outcome, features, or improvements..."
              className="w-full rounded-lg border-indigo-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none resize-none bg-indigo-50"
            />
            <div className="mt-1 text-xs text-gray-500">You can specify what you expect as a fix or improvement.</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Screenshot (Optional)</label>
            <div className="mt-1">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {selectedImage ? (
                    <div className="relative">
                      <img 
                        src={URL.createObjectURL(selectedImage)} 
                        alt="Preview" 
                        className="max-h-24 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          onImageSelect(null);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Click to upload screenshot</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={(e) => onImageSelect(e.target.files[0])}
                />
              </label>
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <button 
              type="button" 
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Submit Defect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditBugModal = ({ isOpen, onClose, onSubmit, isSubmitting, bug }) => {
  if (!isOpen || !bug) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Edit2 className="w-5 h-5 text-indigo-600" />
            Edit Defect
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Defect Title</label>
            <input 
              name="title" 
              required 
              defaultValue={bug.title}
              placeholder="e.g., App crashes on login screen"
              className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select name="severity" defaultValue={bug.severity} className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none">
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Device / OS</label>
              <input 
                name="device" 
                defaultValue={bug.device}
                placeholder="e.g., iPhone 14, iOS 16"
                className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description & Steps to Reproduce</label>
            <textarea 
              name="description" 
              required 
              rows={4}
              defaultValue={bug.description}
              placeholder="1. Open app&#10;2. Click settings&#10;3. Observe crash..."
              className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-indigo-700 mb-1">Desired Solution (Optional)</label>
            <textarea
              name="desiredSolution"
              rows={3}
              defaultValue={bug.desiredSolution}
              placeholder="Describe what solution you want, e.g. expected outcome, features, or improvements..."
              className="w-full rounded-lg border-indigo-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none resize-none bg-indigo-50"
            />
            <div className="mt-1 text-xs text-gray-500">You can specify what you expect as a fix or improvement.</div>
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <button 
              type="button" 
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Update Defect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ViewBugModal = ({ isOpen, onClose, bug }) => {
  if (!isOpen || !bug) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Bug className="w-5 h-5 text-indigo-600" />
            Bug Details
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Title */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{bug.title}</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={bug.status} />
              <SeverityBadge level={bug.severity} />
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                {new Date(bug.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </div>

          {/* Device Info */}
          {bug.device && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-700">
                <Smartphone className="w-5 h-5 text-gray-500" />
                <span className="font-medium">Device:</span>
                <span>{bug.device}</span>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Description & Steps</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{bug.description}</p>
            </div>
          </div>

          {/* Desired Solution */}
          {bug.desiredSolution && (
            <div>
              <h4 className="text-sm font-semibold text-indigo-700 mb-2 uppercase tracking-wide flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Desired Solution
              </h4>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <p className="text-indigo-900 whitespace-pre-wrap leading-relaxed">{bug.desiredSolution}</p>
              </div>
            </div>
          )}

          {/* Screenshot */}
          {bug.imageUrl && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Screenshot</h4>
              <div className="bg-gray-100 rounded-lg p-2">
                <img 
                  src={bug.imageUrl} 
                  alt="Bug screenshot" 
                  className="w-full h-auto rounded-lg shadow-md"
                />
              </div>
            </div>
          )}

          {/* Reporter Info */}
          <div className="border-t pt-4">
            <div className="text-sm text-gray-500">
              <span className="font-medium">Reported by:</span> {bug.reporterId || 'Anonymous'}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Bug ID: {bug._id}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const FeatureRequestModal = ({ isOpen, onClose, onSubmit, isSubmitting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            Request New Feature
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Feature Title</label>
            <input 
              name="title" 
              required 
              placeholder="e.g., Add dark mode support"
              className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select name="priority" className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-amber-500 outline-none">
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select name="category" className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-amber-500 outline-none">
                <option value="UI/UX">UI/UX</option>
                <option value="Performance">Performance</option>
                <option value="Security">Security</option>
                <option value="Integration">Integration</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Feature Description</label>
            <textarea 
              name="description" 
              required 
              rows={5}
              placeholder="Describe the feature you'd like to see...&#10;&#10;- What problem does it solve?&#10;- How would it work?&#10;- Who would benefit from it?"
              className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-amber-500 outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Use Case / Example (Optional)</label>
            <textarea
              name="useCase"
              rows={3}
              placeholder="Provide a real-world scenario or example of how this feature would be used..."
              className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-amber-500 outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name / Email (Optional)</label>
            <input 
              name="requesterInfo" 
              placeholder="e.g., John Doe or john@example.com"
              className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-amber-500 outline-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg shadow-sm transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PriorityBadge = ({ level }) => {
  const styles = {
    Critical: 'bg-red-100 text-red-800 border-red-200',
    High: 'bg-orange-100 text-orange-800 border-orange-200',
    Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    Low: 'bg-green-100 text-green-800 border-green-200',
  };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[level] || styles.Low}`}>
      {level}
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const styles = {
    'UI/UX': 'bg-purple-50 text-purple-700 ring-purple-600/20',
    'Performance': 'bg-blue-50 text-blue-700 ring-blue-600/20',
    'Security': 'bg-red-50 text-red-700 ring-red-600/20',
    'Integration': 'bg-cyan-50 text-cyan-700 ring-cyan-600/20',
    'Other': 'bg-gray-50 text-gray-600 ring-gray-500/10',
  };

  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${styles[category] || styles.Other}`}>
      {category}
    </span>
  );
};

const FeatureStatusBadge = ({ status }) => {
  const styles = {
    'Submitted': 'bg-blue-50 text-blue-700 ring-blue-600/20',
    'Under Review': 'bg-purple-50 text-purple-700 ring-purple-600/20',
    'Planned': 'bg-amber-50 text-amber-700 ring-amber-600/20',
    'In Development': 'bg-cyan-50 text-cyan-700 ring-cyan-600/20',
    'Completed': 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    'Rejected': 'bg-gray-50 text-gray-600 ring-gray-500/10',
  };

  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${styles[status] || styles.Submitted}`}>
      {status}
    </span>
  );
};

// --- Main Application Component ---

export default function BugTracker() {
  const [bugs, setBugs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('bugs'); // 'bugs', 'analysis', or 'features'
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBug, setEditingBug] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingBug, setViewingBug] = useState(null);
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [featureRequests, setFeatureRequests] = useState([]);
  const [featureSearchTerm, setFeatureSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('All');

  // Fetch bugs from API
  const fetchBugs = async (showLoading = false) => {
    try {
      if (showLoading) setIsLoading(true);
      const response = await fetch(`${API_URL}/bugs`);
      if (!response.ok) throw new Error('Failed to fetch bugs');
      const data = await response.json();
      setBugs(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching bugs:', error);
      if (showLoading) {
        setError('Failed to load bugs. Please check if the server is running.');
      }
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    // Pre-warm the server with a lightweight ping (helps reduce cold start time)
    fetch(`${API_URL}/ping`).catch(() => {});
    
    fetchBugs(true); // Load bugs on initial mount
    fetchFeatureRequests(true); // Load feature requests on initial mount
  }, []);

  // Fetch feature requests from API
  const fetchFeatureRequests = async (showLoading = false) => {
    try {
      if (showLoading) setIsLoading(true);
      const response = await fetch(`${API_URL}/features`);
      if (!response.ok) throw new Error('Failed to fetch feature requests');
      const data = await response.json();
      setFeatureRequests(data);
    } catch (error) {
      console.error('Error fetching feature requests:', error);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  // Handle add bug
  const handleAddBug = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);
    
    // Add image if selected
    if (selectedImage) {
      formData.append('image', selectedImage);
    }

    try {
      const response = await fetch(`${API_URL}/bugs`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to create bug');

      await fetchBugs(false); // Refresh the list
      setIsModalOpen(false);
      setSelectedImage(null);
      e.target.reset();
    } catch (error) {
      console.error('Error adding bug:', error);
      alert('Failed to submit bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit bug
  const handleEditBug = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);
    
    try {
      const response = await fetch(`${API_URL}/bugs/${editingBug._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.get('title'),
          severity: formData.get('severity'),
          device: formData.get('device'),
          description: formData.get('description'),
          desiredSolution: formData.get('desiredSolution')
        })
      });

      if (!response.ok) throw new Error('Failed to update bug');

      await fetchBugs(false); // Refresh the list
      setIsEditModalOpen(false);
      setEditingBug(null);
    } catch (error) {
      console.error('Error updating bug:', error);
      alert('Failed to update bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle feature request submission
  const handleFeatureRequest = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);
    const featureData = {
      title: formData.get('title'),
      description: formData.get('description'),
      priority: formData.get('priority'),
      category: formData.get('category'),
      useCase: formData.get('useCase') || '',
      requesterInfo: formData.get('requesterInfo') || 'Anonymous'
    };

    try {
      const response = await fetch(`${API_URL}/features`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(featureData)
      });

      if (!response.ok) throw new Error('Failed to submit feature request');

      await fetchFeatureRequests(false);
      setIsFeatureModalOpen(false);
      e.target.reset();
      alert('Feature request submitted successfully!');
    } catch (error) {
      console.error('Error submitting feature request:', error);
      alert('Failed to submit feature request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open edit modal
  const openEditModal = (bug) => {
    setEditingBug(bug);
    setIsEditModalOpen(true);
  };

  // Open view modal
  const openViewModal = (bug) => {
    setViewingBug(bug);
    setIsViewModalOpen(true);
  };

  // Update bug status
  const updateStatus = async (bugId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/bugs/${bugId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      await fetchBugs(false); // Refresh the list
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  // Delete bug
  const deleteBug = async (bugId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      const response = await fetch(`${API_URL}/bugs/${bugId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete bug');

      await fetchBugs(false); // Refresh the list
    } catch (error) {
      console.error('Error deleting bug:', error);
      alert('Failed to delete bug. Please try again.');
    }
  };

  // Export to CSV
  const exportCSV = () => {
    const headers = ['ID', 'Title', 'Status', 'Severity', 'Device', 'Description', 'Date'];
    const csvContent = [
      headers.join(','),
      ...bugs.map(bug => {
        const date = new Date(bug.createdAt).toLocaleDateString();
        return [
          bug._id,
          `"${bug.title.replace(/"/g, '""')}"`,
          bug.status,
          bug.severity,
          `"${bug.device}"`,
          `"${bug.description.replace(/"/g, '""').replace(/\n/g, ' ')}"`,
          date
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `bug_report_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtered bugs
  const filteredBugs = useMemo(() => {
    return bugs.filter(bug => {
      const matchesSearch = bug.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          bug.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'All' || bug.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [bugs, searchTerm, statusFilter]);

  // Statistics
  const stats = useMemo(() => {
    return {
      total: bugs.length,
      open: bugs.filter(b => b.status === 'Open').length,
      critical: bugs.filter(b => b.severity === 'Critical').length,
      resolved: bugs.filter(b => b.status === 'Resolved').length
    };
  }, [bugs]);

  if (isLoading && bugs.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p>Loading BugTracker...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchBugs}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:h-16 py-3 sm:py-0 sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-600 p-2 rounded-lg">
                {activeTab === 'bugs' ? (
                  <Bug className="w-5 h-5 text-white" />
                ) : activeTab === 'features' ? (
                  <Lightbulb className="w-5 h-5 text-white" />
                ) : (
                  <FileText className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {activeTab === 'bugs' ? 'BugTracker' : activeTab === 'features' ? 'Feature Requests' : 'Project Analysis'}
                </h1>
                {activeTab === 'bugs' && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Talk-And-Heal</span>
                )}
              </div>
            </div>
            
            {/* Navigation Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto">
              <button
                onClick={() => setActiveTab('bugs')}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                  activeTab === 'bugs'
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Bug className="w-4 h-4" />
                <span className="hidden sm:inline">Bug Reports</span>
                <span className="sm:hidden">Bugs</span>
              </button>
              <button
                onClick={() => setActiveTab('analysis')}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                  activeTab === 'analysis'
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Project Analysis</span>
                <span className="sm:hidden">Analysis</span>
              </button>
              <button
                onClick={() => setActiveTab('features')}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                  activeTab === 'features'
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Lightbulb className="w-4 h-4" />
                <span className="hidden sm:inline">Feature Requests</span>
                <span className="sm:hidden">Features</span>
              </button>
            </div>

            {activeTab === 'bugs' && (
              <div className="flex items-center gap-2 sm:gap-3">
                <button 
                  onClick={exportCSV}
                  className="hidden md:flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-all"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-all hover:shadow w-full sm:w-auto justify-center"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Report Issue</span>
                  <span className="sm:hidden">Add Bug</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Conditional Content Based on Active Tab */}
      {activeTab === 'analysis' ? (
        <ProjectAnalysis />
      ) : activeTab === 'features' ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Feature Request Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Feature Requests</h2>
              <p className="text-gray-600 mt-1">Share your ideas and vote on features you'd like to see</p>
            </div>
            <button
              onClick={() => setIsFeatureModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span>New Request</span>
            </button>
          </div>

          {/* Feature Requests List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {featureRequests.length === 0 ? (
              <div className="text-center py-12">
                <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No feature requests yet. Be the first to share your idea!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {featureRequests.map((feature) => (
                  <div key={feature._id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex gap-4">
                      {/* Vote Section */}
                      <div className="flex flex-col items-center gap-1">
                        <button
                          onClick={async () => {
                            try {
                              await fetch(`${API_URL}/features/${feature._id}/vote`, {
                                method: 'PATCH'
                              });
                              fetchFeatureRequests(false);
                            } catch (error) {
                              console.error('Error voting:', error);
                            }
                          }}
                          className="p-2 hover:bg-amber-100 rounded-lg transition-colors"
                        >
                          <ThumbsUp className="w-5 h-5 text-amber-600" />
                        </button>
                        <span className="text-sm font-bold text-gray-700">{feature.votes}</span>
                      </div>

                      {/* Feature Content */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              <FeatureStatusBadge status={feature.status} />
                              <PriorityBadge level={feature.priority} />
                              <CategoryBadge category={feature.category} />
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-700 mb-3">{feature.description}</p>
                        {feature.useCase && (
                          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-3">
                            <p className="text-sm text-blue-900"><strong>Use Case:</strong> {feature.useCase}</p>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>Requested by {feature.requesterInfo}</span>
                          <span>{new Date(feature.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      ) : (
        <>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Issues', value: stats.total, icon: Bug, color: 'text-gray-600', bg: 'bg-gray-100' },
            { label: 'Open Issues', value: stats.open, icon: AlertCircle, color: 'text-blue-600', bg: 'bg-blue-100' },
            { label: 'Critical', value: stats.critical, icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100' },
            { label: 'Resolved', value: stats.resolved, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`p-2 rounded-lg ${stat.bg}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text"
              placeholder="Search issues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
            {['All', 'Open', 'In Progress', 'Resolved', 'Closed'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  statusFilter === status 
                    ? 'bg-gray-900 text-white' 
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Bug List Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Issue</th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">Status</th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Severity</th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-40">Device</th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">Logged</th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-20 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredBugs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Bug className="w-8 h-8 text-gray-300" />
                        <p>No issues found matching your filters.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredBugs.map((bug) => (
                    <tr 
                      key={bug._id} 
                      className="hover:bg-gray-50/50 group transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="flex gap-3">
                          {bug.imageUrl && (
                            <div className="flex-shrink-0">
                              <a 
                                href={bug.imageUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block group/img relative"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <img 
                                  src={bug.imageUrl} 
                                  alt="Bug screenshot" 
                                  className="w-16 h-16 object-cover rounded-lg border border-gray-200 group-hover/img:border-indigo-400 transition-all cursor-pointer"
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/img:bg-black/10 transition-all rounded-lg">
                                  <ImageIcon className="w-5 h-5 text-white opacity-0 group-hover/img:opacity-100 transition-opacity" />
                                </div>
                              </a>
                            </div>
                          )}
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-gray-900">{bug.title}</span>
                            <span className="text-sm text-gray-500 line-clamp-2 mt-0.5">{bug.description}</span>
                            {bug.desiredSolution && (
                              <div className="mt-2 p-2 bg-indigo-50 border-l-2 border-indigo-500 rounded text-xs">
                                <span className="font-semibold text-indigo-900">💡 Desired Solution:</span>
                                <span className="text-indigo-700 ml-1">{bug.desiredSolution}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <select 
                          value={bug.status}
                          onChange={(e) => updateStatus(bug._id, e.target.value)}
                          className="bg-transparent text-xs font-medium outline-none cursor-pointer hover:bg-gray-100 rounded px-1 -ml-1 py-1 transition-colors"
                        >
                          <option value="Open">Open</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Resolved">Resolved</option>
                          <option value="Closed">Closed</option>
                        </select>
                        <div className="mt-1">
                          <StatusBadge status={bug.status} />
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <SeverityBadge level={bug.severity} />
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Smartphone className="w-3.5 h-3.5 text-gray-400" />
                          {bug.device}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          {new Date(bug.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              openViewModal(bug);
                            }}
                            className="text-gray-400 hover:text-blue-600 p-1.5 hover:bg-blue-50 rounded transition-all opacity-0 group-hover:opacity-100"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(bug);
                            }}
                            className="text-gray-400 hover:text-indigo-600 p-1.5 hover:bg-indigo-50 rounded transition-all opacity-0 group-hover:opacity-100"
                            title="Edit Report"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBug(bug._id);
                            }}
                            className="text-gray-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded transition-all opacity-0 group-hover:opacity-100"
                            title="Delete Report"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <AddBugModal 
        isOpen={isModalOpen} 
        onClose={() => {
          setIsModalOpen(false);
          setSelectedImage(null);
        }} 
        onSubmit={handleAddBug}
        isSubmitting={isSubmitting}
        selectedImage={selectedImage}
        onImageSelect={setSelectedImage}
      />

      <EditBugModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingBug(null);
        }}
        onSubmit={handleEditBug}
        isSubmitting={isSubmitting}
        bug={editingBug}
      />

      <ViewBugModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingBug(null);
        }}
        bug={viewingBug}
      />
      </>
      )}

      <FeatureRequestModal
        isOpen={isFeatureModalOpen}
        onClose={() => setIsFeatureModalOpen(false)}
        onSubmit={handleFeatureRequest}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
